<template>
  <div>
    <!-- Simple Navigation Header -->
    <header class="bg-white border-b border-gray-200">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <LogoMain />
            <span class="font-bold text-xl">Better Insights</span>
          </div>
          <div class="flex items-center space-x-4">
            <ButtonMain class="hidden lg:block">
              <router-link to="/"> Back to homepage </router-link>
            </ButtonMain>
            <HamburgerMenu class="lg:hidden" />
          </div>
        </div>
      </div>
    </header>

    <!-- Terms and Conditions Content -->
    <section class="bg-white py-16 lg:py-24">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-4xl mx-auto">
          <!-- Page Header -->
          <div class="text-center mb-12">
            <h1 class="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Terms and Conditions
            </h1>
            <p class="text-lg text-gray-600">Last updated: {{ currentDate }}</p>
          </div>

          <!-- Terms Content -->
          <div class="prose prose-lg max-w-none">
            <div class="space-y-8">
              <!-- Introduction -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  1. Introduction
                </h2>
                <p class="text-gray-700 mb-4">
                  Welcome to Better Insights. These Terms and Conditions
                  ("Terms") govern your use of our website and services. By
                  accessing or using our website, you agree to be bound by these
                  Terms.
                </p>
                <p class="text-gray-700">
                  Better Insights is a data analytics and insights platform
                  operated by TCR Labs. These Terms apply to all visitors,
                  users, and others who access or use our services.
                </p>
              </section>

              <!-- Acceptance of Terms -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  2. Acceptance of Terms
                </h2>
                <p class="text-gray-700 mb-4">
                  By accessing and using this website, you accept and agree to
                  be bound by the terms and provision of this agreement. If you
                  do not agree to abide by the above, please do not use this
                  service.
                </p>
              </section>

              <!-- Use License -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  3. Use License
                </h2>
                <p class="text-gray-700 mb-4">
                  Permission is granted to temporarily download one copy of the
                  materials (information or software) on Better Insights's
                  website for personal, non-commercial transitory viewing only.
                  This is the grant of a license, not a transfer of title, and
                  under this license you may not:
                </p>
                <ul class="list-disc pl-6 text-gray-700 space-y-2">
                  <li>modify or copy the materials;</li>
                  <li>
                    use the materials for any commercial purpose or for any
                    public display (commercial or non-commercial);
                  </li>
                  <li>
                    attempt to decompile or reverse engineer any software
                    contained on Better Insights's website;
                  </li>
                  <li>
                    remove any copyright or other proprietary notations from the
                    materials; or
                  </li>
                  <li>
                    transfer the materials to another person or "mirror" the
                    materials on any other server.
                  </li>
                </ul>
              </section>

              <!-- Disclaimer -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  4. Disclaimer
                </h2>
                <p class="text-gray-700 mb-4">
                  The materials on Better Insights's website are provided on an
                  'as is' basis. Better Insights makes no warranties, expressed
                  or implied, and hereby disclaims and negates all other
                  warranties including without limitation, implied warranties or
                  conditions of merchantability, fitness for a particular
                  purpose, or non-infringement of intellectual property or other
                  violation of rights.
                </p>
              </section>

              <!-- Limitations -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  5. Limitations
                </h2>
                <p class="text-gray-700 mb-4">
                  In no event shall Better Insights or its suppliers be liable
                  for any damages (including, without limitation, damages for
                  loss of data or profit, or due to business interruption)
                  arising out of the use or inability to use the materials on
                  Better Insights's website, even if Better Insights or a Better
                  Insights authorized representative has been notified orally or
                  in writing of the possibility of such damage.
                </p>
              </section>

              <!-- Accuracy of Materials -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  6. Accuracy of Materials
                </h2>
                <p class="text-gray-700 mb-4">
                  The materials appearing on Better Insights's website could
                  include technical, typographical, or photographic errors.
                  Better Insights does not warrant that any of the materials on
                  its website are accurate, complete or current. Better Insights
                  may make changes to the materials contained on its website at
                  any time without notice.
                </p>
              </section>

              <!-- Links -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  7. Links
                </h2>
                <p class="text-gray-700 mb-4">
                  Better Insights has not reviewed all of the sites linked to
                  its website and is not responsible for the contents of any
                  such linked site. The inclusion of any link does not imply
                  endorsement by Better Insights of the site. Use of any such
                  linked website is at the user's own risk.
                </p>
              </section>

              <!-- Modifications -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  8. Modifications
                </h2>
                <p class="text-gray-700 mb-4">
                  Better Insights may revise these terms of service for its
                  website at any time without notice. By using this website you
                  are agreeing to be bound by the then current version of these
                  Terms and Conditions of Use.
                </p>
              </section>

              <!-- Governing Law -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  9. Governing Law
                </h2>
                <p class="text-gray-700 mb-4">
                  These terms and conditions are governed by and construed in
                  accordance with the laws and you irrevocably submit to the
                  exclusive jurisdiction of the courts in that state or
                  location.
                </p>
              </section>

              <!-- Privacy Policy -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  10. Privacy Policy
                </h2>
                <p class="text-gray-700 mb-4">
                  Your privacy is important to us. Please review our Privacy
                  Policy, which also governs your use of the website, to
                  understand our practices.
                </p>
              </section>

              <!-- Contact Information -->
              <section>
                <h2 class="text-2xl font-semibold text-gray-900 mb-4">
                  11. Contact Information
                </h2>
                <p class="text-gray-700 mb-4">
                  If you have any questions about these Terms and Conditions,
                  please contact us at:
                </p>
                <div class="bg-gray-50 p-4 rounded-lg">
                  <p class="text-gray-700">
                    <strong>Better Insights</strong><br />
                    A TCR Labs company<br />
                    Website: www.betterinsights.co
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import Footer from "@/components/sections/HomeFooter.vue";
import LogoMain from "@/components/shared/LogoMain.vue";
import ButtonMain from "@/components/shared/ButtonMain.vue";
import HamburgerMenu from "@/components/shared/HamburgerMenu.vue";
import { ref } from "vue";

const currentDate = ref(
  new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
);
</script>
